import { type ClipboardEvent, type KeyboardEvent, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";

export interface OtpInputProps {
  value: string;
  onChange: (next: string) => void;
  /** Fired when the boxes fill to `length` (typed last char or pasted). */
  onComplete?: (value: string) => void;
  length?: number;
  inputMode?: "numeric" | "text";
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  /** Applied to the first box so a <Field htmlFor> label targets it. */
  id?: string;
  ariaLabel?: string;
}

// OtpInput — segmented one-time-code entry: N single-character boxes with
// auto-advance, Backspace / Arrow navigation, and full-code paste (paste a
// 6-digit code into any box and it spreads across all of them, the way modern
// web auth flows do). The value is a plain contiguous string owned by the
// caller; the boxes are derived from it, so it drops straight into an existing
// `code` state with no extra plumbing.
export function OtpInput({
  value,
  onChange,
  onComplete,
  length = 6,
  inputMode = "numeric",
  disabled,
  invalid,
  autoFocus,
  id,
  ariaLabel,
}: OtpInputProps) {
  const { t } = useTranslation("ui");
  const boxes = useRef<Array<HTMLInputElement | null>>([]);
  const sanitize = (s: string) => s.replace(inputMode === "numeric" ? /[^0-9]/g : /[^0-9a-zA-Z]/g, "");
  const chars = value.split("");

  const focusBox = (i: number) => {
    const el = boxes.current[Math.max(0, Math.min(length - 1, i))];
    el?.focus();
    el?.select();
  };

  // push is the single mutation path: clean, clamp to length, lift to the
  // caller, and fire onComplete on the transition to a full code.
  const push = (next: string) => {
    const v = sanitize(next).slice(0, length);
    onChange(v);
    if (v.length === length) onComplete?.(v);
    return v;
  };

  const handleChange = (i: number, raw: string) => {
    const cleaned = sanitize(raw);
    if (cleaned === "") {
      push(value.slice(0, i) + value.slice(i + 1));
      return;
    }
    if (cleaned.length === 1) {
      push(value.slice(0, i) + cleaned + value.slice(i + 1));
      focusBox(i + 1);
      return;
    }
    // Autofill / multi-char landing in one box — splice in from here.
    push(value.slice(0, i) + cleaned);
    focusBox(i + cleaned.length);
  };

  const handleKeyDown = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (chars[i]) {
        push(value.slice(0, i) + value.slice(i + 1));
        focusBox(i);
      } else if (i > 0) {
        push(value.slice(0, i - 1) + value.slice(i));
        focusBox(i - 1);
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusBox(i - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusBox(i + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = sanitize(e.clipboardData.getData("text"));
    if (!pasted) return;
    const v = push(pasted);
    focusBox(v.length >= length ? length - 1 : v.length);
  };

  return (
    <div role="group" aria-label={ariaLabel ?? t("otp.verificationCode")} className="flex items-center gap-2">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            boxes.current[i] = el;
          }}
          id={i === 0 ? id : undefined}
          type="text"
          inputMode={inputMode}
          autoComplete={i === 0 ? "one-time-code" : "off"}
          aria-label={t("otp.character", { index: i + 1, total: length })}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          maxLength={1}
          value={chars[i] ?? ""}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.currentTarget.select()}
          className={cn(
            "h-12 w-0 min-w-0 flex-1 rounded-md border bg-input text-center text-lg font-semibold text-fg tabular-nums transition-colors",
            "focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring/45",
            "disabled:cursor-not-allowed disabled:opacity-50",
            invalid ? "border-danger focus-visible:border-danger focus-visible:ring-danger/35" : "border-border",
          )}
        />
      ))}
    </div>
  );
}
